import { Close, CloudUpload } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Box, styled, useTheme } from "@mui/system";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";

const ACCEPT = {
  excel: {
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
  },
};

const SUPPORTED_FILE_TYPES = {
  excel: "Supported file types: .xls, .xlsx",
};

export type UploadDialogProps = {
  isOpen: boolean;
};

export type UploadDialogEvents =
  | "upload-dialog:close"
  | "upload-dialog:upload-planned-rates"
  | "upload-dialog:upload-actual-rates";

export type UploadDialogEventPayload = {
  file: File;
};

export type UploadDialogFireEventType =
  | ((event: UploadDialogEvents, payload?: UploadDialogEventPayload) => void)
  | undefined;

export const UploadDialogFXRates: PimoReactComponent<
  UploadDialogProps,
  UploadDialogEvents,
  UploadDialogEventPayload
> = ({ isOpen, fireEvent }) => {
  const [plannedRatesFile, setPlannedRatesFile] = useState<File | null>(null);
  const [actualRatesFile, setActualRatesFile] = useState<File | null>(null);

  const handleClose = () => fireEvent?.("upload-dialog:close");

  const handleUploadPlannedRates = () => {
    if (plannedRatesFile) {
      fireEvent?.("upload-dialog:upload-planned-rates", {
        file: plannedRatesFile,
      });
      setPlannedRatesFile(null);
    }
  };

  const handleUploadActualRates = () => {
    if (actualRatesFile) {
      fireEvent?.("upload-dialog:upload-actual-rates", {
        file: actualRatesFile,
      });
      setActualRatesFile(null);
    }
  };

  const onDropPlannedRates = useCallback((droppedFiles: File[]) => {
    if (droppedFiles) {
      setPlannedRatesFile(droppedFiles[0]);
    }
  }, []);

  const onDropActualRates = useCallback((droppedFiles: File[]) => {
    if (droppedFiles) {
      setActualRatesFile(droppedFiles[0]);
    }
  }, []);

  const plannedRatesDropzone = useDropzone({
    multiple: false,
    accept: ACCEPT.excel,
    onDrop: onDropPlannedRates,
  });

  const actualRatesDropzone = useDropzone({
    multiple: false,
    accept: ACCEPT.excel,
    onDrop: onDropActualRates,
  });

  return (
    <Dialog
      sx={{
        margin: "50px",
        zIndex: 9999,
      }}
      PaperProps={{ sx: { borderRadius: "12px" } }}
      maxWidth="md"
      open={isOpen}
      onClose={handleClose}
      scroll="paper"
    >
      <DialogHeader>
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "22px",
          }}
        >
          FX Rates Upload
        </DialogTitle>
        <Box marginY="auto">
          <IconButton aria-label="close" onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogHeader>
      <DialogContent sx={{ padding: 0, borderBottom: "none" }} dividers>
        {/* Planned Rates Upload Section */}
        <UploadSection>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, marginBottom: "16px" }}
          >
            Planned Rates
          </Typography>
          <ContentBox
            onClick={() => {
              if (plannedRatesDropzone.inputRef.current) {
                plannedRatesDropzone.inputRef.current.click();
              }
            }}
            {...plannedRatesDropzone.getRootProps()}
          >
            <DropZoneBox
              selectedFile={plannedRatesFile}
              isDragActive={plannedRatesDropzone.isDragActive}
              supportedFileTypes={SUPPORTED_FILE_TYPES.excel}
            />
            <input
              type="file"
              name="plannedRatesUpload"
              onChange={(event) => {
                const files = event.currentTarget.files;
                if (files) {
                  setPlannedRatesFile(Array.from(files)[0]);
                }
              }}
              {...plannedRatesDropzone.getInputProps()}
            />
          </ContentBox>
          <Button
            variant="contained"
            sx={{ borderRadius: "4px", color: "#FFF", marginTop: "16px" }}
            onClick={handleUploadPlannedRates}
            disabled={!plannedRatesFile}
          >
            Upload Planned Rates
          </Button>
        </UploadSection>

        {/* Actual Rates Upload Section */}
        <UploadSection>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, marginBottom: "16px" }}
          >
            Actual Rates
          </Typography>
          <ContentBox
            onClick={() => {
              if (actualRatesDropzone.inputRef.current) {
                actualRatesDropzone.inputRef.current.click();
              }
            }}
            {...actualRatesDropzone.getRootProps()}
          >
            <DropZoneBox
              selectedFile={actualRatesFile}
              isDragActive={actualRatesDropzone.isDragActive}
              supportedFileTypes={SUPPORTED_FILE_TYPES.excel}
            />
            <input
              type="file"
              name="actualRatesUpload"
              onChange={(event) => {
                const files = event.currentTarget.files;
                if (files) {
                  setActualRatesFile(Array.from(files)[0]);
                }
              }}
              {...actualRatesDropzone.getInputProps()}
            />
          </ContentBox>
          <Button
            variant="contained"
            sx={{ borderRadius: "4px", color: "#FFF", marginTop: "16px" }}
            onClick={handleUploadActualRates}
            disabled={!actualRatesFile}
          >
            Upload Actual Rates
          </Button>
        </UploadSection>
      </DialogContent>
      <DialogActions
        sx={{
          margin: "32px",
        }}
      >
        <Button
          sx={(theme) => ({
            borderRadius: "4px",
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          })}
          onClick={handleClose}
          variant="outlined"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogHeader = styled(Box)(({ theme }) => ({
  marginLeft: "40px",
  display: "flex",
  justifyContent: "space-between",
  paddingRight: theme.spacing(2),
}));

const UploadSection = styled(Box)({
  padding: "24px 32px",
  borderBottom: "1px solid #e0e0e0",
  "&:last-child": {
    borderBottom: "none",
  },
});

function DropZoneBox({
  selectedFile,
  isDragActive,
  supportedFileTypes,
}: {
  selectedFile: File | null;
  isDragActive: boolean;
  supportedFileTypes: string;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "656px",
      }}
    >
      {selectedFile && (
        <Typography key={selectedFile.name}>
          Selected File: {selectedFile.name}
        </Typography>
      )}
      <CloudUpload
        sx={{
          color: theme.palette.primary.main,
          width: "53px",
          height: "53px",
        }}
      />
      <Typography sx={{ marginBottom: "8px" }}>
        {isDragActive
          ? "Drop the files here..."
          : "Drag & drop file or select from computer"}
      </Typography>
      <Typography color={theme.palette.grey[700]}>
        {supportedFileTypes}
      </Typography>
    </Box>
  );
}

const ContentBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "8px",
  alignItems: "center",
  width: "100%",
  minHeight: "143px",
  border: "1px dotted blue",
  borderRadius: "8px",
  padding: "8px",
  textTransform: "unset",
  cursor: "pointer",
});
